/**
 * STAGE 1 — Redux Slice (createSlice)
 * -----------------------------------
 * A "slice" is one piece of global state + the functions that change it.
 *
 * Redux Toolkit gives you:
 *   state        → the single source of truth (replaces scattered useState)
 *   reducers     → synchronous updates (dispatch(updateTopHeader('User Manager')))
 *   extraReducers → react to async thunks (fetchAppData.fulfilled, etc.)
 *
 * NEVER mutate state directly in components — always dispatch an action.
 */
import { createSlice } from '@reduxjs/toolkit'
import { fetchAppData } from './appDataThunks'

const initialState = {
  topHeaderText: '',
  loading: false,
  userData: {},
  permission: {},
  // Session-wide layout data (sidebar permissions already above; header balances here)
  appData: {
    totalChips: 0,
    totalCash: 0,
    profileImage: '',
    isLoaded: false,
    loading: false,
    error: null,
    lastFetchedAt: null
  },
  dashboard: {
    dashboardData: {},
    paymentsData: null,
    dauData: [],
    peakData: []
  },
  grades: {
    pages: {},
    pagerecord: {
      totalItems: 0,
      totalPages: 1,
      pageNumber: 1,
      pageSize: 5
    },
    isLoaded: false
  },
  bankWithdraw: {
    pages: {},
    pagerecord: {
      totalItems: 0,
      pageNumber: 1,
      pageSize: 25,
      totalPages: 1
    },
    isLoaded: false
  },
  videos: {
    pages: {},
    pagerecord: {
      totalItems: 0,
      pageNumber: 1,
      pageSize: 12,
      totalPages: 1
    },
    isLoaded: false
  },
  users: {
    pages: {},
    pagerecord: {
      totalItems: 0,
      pageNumber: 1,
      pageSize: 25,
      totalPages: 1
    },
    isLoaded: false
  }
}

export const commonSlice = createSlice({
  name: 'common',
  initialState,
  reducers: {
    updateTopHeader: (state, action) => {
      state.topHeaderText = action.payload
    },
    uplodateLoading: (state, action) => {
      state.loading = action.payload
    },
    updateUserData: (state, action) => {
      state.userData = action.payload
    },
    updatePermission: (state, action) => {
      state.permission = action.payload
    },
    // Called on logout — wipe session layout cache
    resetAppData: (state) => {
      state.appData = { ...initialState.appData }
      state.permission = {}
      state.userData = {}
      state.topHeaderText = ''
      state.dashboard = { ...initialState.dashboard }
      state.grades = { ...initialState.grades }
      state.bankWithdraw = { ...initialState.bankWithdraw }
      state.videos = { ...initialState.videos }
      state.users = { ...initialState.users }
    },
    updateDashboardData: (state, action) => {
      state.dashboard.dashboardData = action.payload
    },
    updatePaymentsData: (state, action) => {
      state.dashboard.paymentsData = action.payload
    },
    updateDauData: (state, action) => {
      state.dashboard.dauData = action.payload
    },
    updatePeakData: (state, action) => {
      state.dashboard.peakData = action.payload
    },
    updateGrades: (state, action) => {
      const { pageNumber, content, pagerecord } = action.payload
      if (!state.grades.pages) {
        state.grades.pages = {}
      }
      state.grades.pages[pageNumber] = content
      state.grades.pagerecord = pagerecord
      state.grades.isLoaded = true
    },
    clearGradesCache: (state) => {
      state.grades.pages = {}
      state.grades.isLoaded = false
    },
    updateBankWithdraw: (state, action) => {
      const { pageNumber, datalist, pagerecord } = action.payload
      if (!state.bankWithdraw.pages) {
        state.bankWithdraw.pages = {}
      }
      state.bankWithdraw.pages[pageNumber] = datalist
      state.bankWithdraw.pagerecord = pagerecord
      state.bankWithdraw.isLoaded = true
    },
    clearBankWithdrawCache: (state) => {
      state.bankWithdraw.pages = {}
      state.bankWithdraw.isLoaded = false
    },
    updateSingleWithdrawStatus: (state, action) => {
      const { txnId, status } = action.payload
      if (state.bankWithdraw.pages) {
        Object.keys(state.bankWithdraw.pages).forEach((pageKey) => {
          state.bankWithdraw.pages[pageKey] = state.bankWithdraw.pages[pageKey].map((item) => {
            if (item.txnId === txnId) {
              return { ...item, status: status }
            }
            return item
          })
        })
      }
    },
    updateMultipleWithdrawStatus: (state, action) => {
      const { txnIds, status } = action.payload
      const idsSet = new Set(txnIds)
      if (state.bankWithdraw.pages) {
        Object.keys(state.bankWithdraw.pages).forEach((pageKey) => {
          state.bankWithdraw.pages[pageKey] = state.bankWithdraw.pages[pageKey].map((item) => {
            if (idsSet.has(item.txnId)) {
              return { ...item, status: status }
            }
            return item
          })
        })
      }
    },
    updateVideos: (state, action) => {
      const { pageNumber, datalist, pagerecord } = action.payload
      if (!state.videos.pages) {
        state.videos.pages = {}
      }
      state.videos.pages[pageNumber] = datalist
      state.videos.pagerecord = pagerecord
      state.videos.isLoaded = true
    },
    clearVideosCache: (state) => {
      state.videos.pages = {}
      state.videos.isLoaded = false
    },
    updateSingleVideoStatus: (state, action) => {
      const { id, active } = action.payload
      if (state.videos.pages) {
        Object.keys(state.videos.pages).forEach((pageKey) => {
          state.videos.pages[pageKey] = state.videos.pages[pageKey].map((item) => {
            if (item.id === id) {
              return { ...item, active: active }
            }
            return item
          })
        })
      }
    },
    updateMultipleVideosRewarded: (state, action) => {
      const { giveRewardOnVideos, actionType } = action.payload
      const videoMap = new Map(giveRewardOnVideos.map((v) => [v.videoId, v.chips]))
      if (state.videos.pages) {
        Object.keys(state.videos.pages).forEach((pageKey) => {
          state.videos.pages[pageKey] = state.videos.pages[pageKey].map((item) => {
            if (videoMap.has(item.id)) {
              const updatedItem = { ...item }
              if (actionType === 'ONLY_REWARD' || actionType === 'APPROVE_REWARD') {
                updatedItem.rewarded = true
                updatedItem.videoReward = videoMap.get(item.id)
              }
              if (actionType === 'ONLY_APPROVE' || actionType === 'APPROVE_REWARD') {
                updatedItem.approved = true
              }
              return updatedItem
            }
            return item
          })
        })
      }
    },
    updateUsers: (state, action) => {
      const { pageNumber, datalist, pagerecord } = action.payload
      if (!state.users) {
        state.users = { pages: {}, pagerecord: {}, isLoaded: false }
      }
      if (!state.users.pages) {
        state.users.pages = {}
      }
      state.users.pages[pageNumber] = datalist
      state.users.pagerecord = pagerecord
      state.users.isLoaded = true
    },
    clearUsersCache: (state) => {
      if (!state.users) {
        state.users = { pages: {}, pagerecord: {}, isLoaded: false }
      }
      state.users.pages = {}
      state.users.isLoaded = false
    },
    updateSingleUserStatus: (state, action) => {
      const { id, active } = action.payload
      if (state.users && state.users.pages) {
        Object.keys(state.users.pages).forEach((pageKey) => {
          state.users.pages[pageKey] = state.users.pages[pageKey].map((item) => {
            if (item.id === id) {
              return { ...item, active: active }
            }
            return item
          })
        })
      }
    },
    updateSingleUserCelebrity: (state, action) => {
      const { id, badged } = action.payload
      if (state.users && state.users.pages) {
        Object.keys(state.users.pages).forEach((pageKey) => {
          state.users.pages[pageKey] = state.users.pages[pageKey].map((item) => {
            if (item.id === id) {
              return { ...item, badged: badged }
            }
            return item
          })
        })
      }
    },
    updateSingleUserGrade: (state, action) => {
      const { id, gradeId, gradeName } = action.payload
      if (state.users && state.users.pages) {
        Object.keys(state.users.pages).forEach((pageKey) => {
          state.users.pages[pageKey] = state.users.pages[pageKey].map((item) => {
            if (item.id === id) {
              return { ...item, gradeId: gradeId, gradeName: gradeName }
            }
            return item
          })
        })
      }
    },
    updateSingleUserBeads: (state, action) => {
      const { userId, beads, debitType } = action.payload
      const amount = parseInt(beads) || 0
      if (state.users && state.users.pages) {
        Object.keys(state.users.pages).forEach((pageKey) => {
          state.users.pages[pageKey] = state.users.pages[pageKey].map((item) => {
            if (item.userId === userId) {
              const currentBalance = item.balance || 0
              const newBalance = debitType ? (currentBalance - amount) : (currentBalance + amount)
              return { ...item, balance: newBalance }
            }
            return item
          })
        })
      }
    },
    // Edit profile can patch image without a full refetch
    updateProfileImage: (state, action) => {
      state.appData.profileImage = action.payload
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAppData.pending, (state) => {
        state.appData.loading = true
        state.appData.error = null
      })
      .addCase(fetchAppData.fulfilled, (state, action) => {
        const { permission, profileImage, totalChips, totalCash } = action.payload
        if (permission) {
          state.permission = permission
        }
        state.appData.profileImage = profileImage
        state.appData.totalChips = totalChips
        state.appData.totalCash = totalCash
        state.appData.isLoaded = true
        state.appData.loading = false
        state.appData.lastFetchedAt = Date.now()
      })
      .addCase(fetchAppData.rejected, (state, action) => {
        state.appData.loading = false
        state.appData.error = action.payload || action.error?.message
      })
  }
})

export const {
  updateTopHeader,
  uplodateLoading,
  updateUserData,
  updatePermission,
  resetAppData,
  updateProfileImage,
  updateDashboardData,
  updatePaymentsData,
  updateDauData,
  updatePeakData,
  updateGrades,
  updateBankWithdraw,
  clearGradesCache,
  clearBankWithdrawCache,
  updateSingleWithdrawStatus,
  updateMultipleWithdrawStatus,
  updateVideos,
  clearVideosCache,
  updateSingleVideoStatus,
  updateMultipleVideosRewarded,
  updateUsers,
  clearUsersCache,
  updateSingleUserStatus,
  updateSingleUserCelebrity,
  updateSingleUserGrade,
  updateSingleUserBeads
} = commonSlice.actions

export { fetchAppData } from './appDataThunks'

export default commonSlice.reducer
